<template>
  <div class="exception-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">OPERATIONS CONTROL</p>
        <h1>异常处置中心</h1>
        <p>统一查看 SDK 离线、订单停滞、仲裁冻结和派单兜底。页面只展示处置所需信息，不展示设备密钥或原始上报内容。</p>
      </div>
      <button class="primary-btn" :disabled="loading" @click="loadExceptions">刷新异常</button>
    </header>

    <section class="metric-grid">
      <article class="metric-card total"><span>待处置</span><strong>{{ summary.total || 0 }}</strong><p>当前聚合异常</p></article>
      <article class="metric-card critical"><span>严重</span><strong>{{ summary.critical || 0 }}</strong><p>仲裁及高风险阻塞</p></article>
      <article class="metric-card high"><span>高优先级</span><strong>{{ summary.high || 0 }}</strong><p>离线与订单停滞</p></article>
      <article class="metric-card medium"><span>需关注</span><strong>{{ summary.medium || 0 }}</strong><p>候选池与派单兜底</p></article>
    </section>

    <section class="category-strip">
      <button v-for="item in categories" :key="item.value" :class="{ active: category === item.value }" @click="selectCategory(item.value)">
        <span>{{ item.label }}</span>
        <strong>{{ countFor(item.value) }}</strong>
      </button>
    </section>

    <section class="threshold-panel">
      <div>
        <p class="eyebrow">ALERT THRESHOLDS</p>
        <h2>异常判断阈值</h2>
      </div>
      <label><span>SDK 离线</span><el-input-number v-model="thresholdForm.sdkOfflineThresholdSeconds" :min="60" :max="86400" :step="60" size="small"></el-input-number><em>秒</em></label>
      <label><span>订单停滞</span><el-input-number v-model="thresholdForm.orderStalledThresholdSeconds" :min="300" :max="604800" :step="300" size="small"></el-input-number><em>秒</em></label>
      <button class="primary-btn" :disabled="savingThresholds" @click="saveThresholds">保存阈值</button>
    </section>

    <section class="table-panel">
      <div class="panel-head">
        <div>
          <p class="eyebrow">ACTION QUEUE</p>
          <h2>异常队列</h2>
        </div>
        <span>生成时间：{{ formatTime(generatedAt) }}</span>
      </div>
      <el-table v-loading="loading" :data="records" empty-text="当前没有需要人工处理的异常">
        <el-table-column label="优先级" width="108">
          <template #default="{ row }"><el-tag :type="severityType(row.severity)" effect="dark">{{ severityText(row.severity) }}</el-tag></template>
        </el-table-column>
        <el-table-column label="异常" min-width="220">
          <template #default="{ row }"><strong>{{ row.title }}</strong><small>{{ categoryText(row.category) }} · {{ row.reason }}</small></template>
        </el-table-column>
        <el-table-column label="关联对象" min-width="220">
          <template #default="{ row }">
            <span v-if="row.robotName">{{ row.robotName }} <small>#{{ row.robotId }}</small></span>
            <span v-else-if="row.taskTitle">{{ row.taskTitle }} <small>#{{ row.taskId }}</small></span>
            <span v-else>订单 #{{ row.orderId || '-' }} <small>任务 #{{ row.taskId || '-' }}</small></span>
            <small v-if="row.orderStatus">订单状态：{{ row.orderStatus }}{{ row.workflowStatus ? ` / ${row.workflowStatus}` : '' }}</small>
            <small v-if="row.expectedRobotType">期望类型：{{ row.expectedRobotType }}</small>
          </template>
        </el-table-column>
        <el-table-column label="发生情况" min-width="180">
          <template #default="{ row }">
            <span>{{ formatTime(row.lastOccurredAt) }}</span>
            <small v-if="row.ageSeconds !== null && row.ageSeconds !== undefined">已持续 {{ formatAge(row.ageSeconds) }}</small>
            <small v-else-if="row.noCandidateCount">连续 {{ row.noCandidateCount }} 次无候选</small>
          </template>
        </el-table-column>
        <el-table-column label="建议" min-width="240"><template #default="{ row }"><span class="suggestion">{{ row.suggestedAction }}</span></template></el-table-column>
        <el-table-column label="处置状态" width="130"><template #default="{ row }"><el-tag :type="row.handlingStatus === 'ACKNOWLEDGED' ? 'warning' : 'danger'">{{ handlingText(row.handlingStatus) }}</el-tag><small v-if="row.handlingNote">{{ row.handlingNote }}</small></template></el-table-column>
        <el-table-column label="操作" width="330" fixed="right">
          <template #default="{ row }">
            <el-button v-if="row.handlingStatus !== 'ACKNOWLEDGED'" size="mini" @click="handleException(row, 'ACKNOWLEDGED')">确认</el-button>
            <el-button size="mini" type="success" @click="handleException(row, 'RESOLVED')">解决</el-button>
            <el-button v-if="hasAction(row, 'RETRY_DISPATCH')" size="mini" type="success" @click="retryDispatch(row)">重新派单</el-button>
            <el-button v-if="hasAction(row, 'OPEN_GRAB')" size="mini" @click="openGrab(row)">开放抢单</el-button>
            <el-button v-if="hasAction(row, 'PAUSE_ROBOT')" size="mini" type="warning" @click="pauseRobot(row)">暂停接单</el-button>
            <el-button v-if="hasAction(row, 'OPEN_ARBITRATION')" size="mini" type="danger" @click="$router.push({ name: 'adminDerasReviews' })">处理仲裁</el-button>
            <el-button v-if="hasAction(row, 'OPEN_ORDER')" size="mini" @click="$router.push({ name: 'adminOrders' })">查看订单</el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <section class="table-panel history-panel">
      <div class="panel-head"><div><p class="eyebrow">AUDIT HISTORY</p><h2>处置记录</h2></div><span>最近 {{ history.length }} 条</span></div>
      <el-table :data="history" empty-text="暂无处置记录">
        <el-table-column prop="id" label="记录" width="80"></el-table-column>
        <el-table-column label="异常类别" width="150"><template #default="{ row }">{{ categoryText(row.category) }}</template></el-table-column>
        <el-table-column label="关联对象" min-width="180"><template #default="{ row }"><span v-if="row.robotId">机器人 #{{ row.robotId }}</span><span v-else-if="row.orderId">订单 #{{ row.orderId }}</span><span v-else>任务 #{{ row.taskId || '-' }}</span></template></el-table-column>
        <el-table-column label="状态" width="110"><template #default="{ row }"><el-tag :type="row.handlingStatus === 'RESOLVED' ? 'success' : 'warning'">{{ handlingText(row.handlingStatus) }}</el-tag></template></el-table-column>
        <el-table-column prop="handlingNote" label="处理说明" min-width="260"></el-table-column>
        <el-table-column label="处理人/时间" min-width="190"><template #default="{ row }"><span>{{ row.handledByAdminName || `管理员 #${row.handledByAdminId || '-'}` }}</span><small>{{ formatTime(row.updateTime) }}</small></template></el-table-column>
      </el-table>
    </section>
  </div>
</template>

<script>
import { getApiErrorMessage } from '../api/http'
import { getAdminOperationExceptions, handleAdminOperationException, openGrabAdminDispatch, pauseAdminRobotOrders, retryAdminDispatch, saveDerasSettings } from '../api/market'

export default {
  name: 'AdminExceptions',
  data() {
    return { loading: false, savingThresholds: false, category: '', records: [], history: [], summary: {}, generatedAt: null,
      thresholds: {}, thresholdForm: { sdkOfflineThresholdSeconds: 300, orderStalledThresholdSeconds: 1800 },
      categories: [
        { value: '', label: '全部' }, { value: 'SDK_OFFLINE', label: 'SDK 离线' },
        { value: 'ORDER_STALLED', label: '订单停滞' }, { value: 'ARBITRATION', label: '仲裁冻结' },
        { value: 'DISPATCH_FALLBACK', label: '派单兜底' }
      ] }
  },
  created() { this.loadExceptions() },
  methods: {
    async loadExceptions() {
      this.loading = true
      try {
        const response = await getAdminOperationExceptions({ category: this.category || undefined })
        if (response.data.code !== 200) { this.$message.error(response.data.msg || '异常队列加载失败'); return }
        const data = response.data.data || {}
        this.records = data.records || []
        this.summary = data.summary || {}
        this.generatedAt = data.generatedAt || null
        this.history = data.history || []
        this.thresholds = data.thresholds || {}
        this.thresholdForm.sdkOfflineThresholdSeconds = Number(this.thresholds.sdkOfflineThresholdSeconds || 300)
        this.thresholdForm.orderStalledThresholdSeconds = Number(this.thresholds.orderStalledThresholdSeconds || 1800)
      } catch (error) { this.$message.error(error.userMessage || getApiErrorMessage(error, '异常队列请求失败')) } finally { this.loading = false }
    },
    selectCategory(value) { this.category = value; this.loadExceptions() },
    hasAction(row, action) { return (row.actions || []).includes(action) },
    countFor(value) {
      if (!value) return this.summary.total || 0
      const map = { SDK_OFFLINE: 'sdkOffline', ORDER_STALLED: 'stalledOrders', ARBITRATION: 'arbitrations', DISPATCH_FALLBACK: 'dispatchFallbacks' }
      return this.summary[map[value]] || 0
    },
    categoryText(value) { return ({ SDK_OFFLINE: 'SDK 离线', ORDER_STALLED: '订单停滞', ARBITRATION: '仲裁冻结', DISPATCH_FALLBACK: '派单兜底' })[value] || value },
    severityText(value) { return ({ CRITICAL: '严重', HIGH: '高', MEDIUM: '中' })[value] || '低' },
    severityType(value) { return ({ CRITICAL: 'danger', HIGH: 'warning', MEDIUM: 'info' })[value] || '' },
    handlingText(value) { return ({ OPEN: '待处置', ACKNOWLEDGED: '已确认', RESOLVED: '已解决' })[value] || value },
    formatTime(value) { if (!value) return '暂无时间记录'; const date = new Date(value); return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('zh-CN', { hour12: false }) },
    formatAge(seconds) { const value = Number(seconds || 0); if (value < 60) return `${value} 秒`; if (value < 3600) return `${Math.floor(value / 60)} 分钟`; return `${Math.floor(value / 3600)} 小时 ${Math.floor((value % 3600) / 60)} 分钟` },
    async retryDispatch(row) { await this.runAction(() => retryAdminDispatch(row.taskId), '已重新发起自动派单') },
    async openGrab(row) { await this.runAction(() => openGrabAdminDispatch(row.taskId), '任务已开放抢单') },
    async pauseRobot(row) {
      try { await this.$confirm(`确认暂停“${row.robotName || `机器人 #${row.robotId}`}”后续接单吗？进行中的订单不会中断。`, '暂停接单', { type: 'warning' }) } catch (error) { return }
      await this.runAction(() => pauseAdminRobotOrders(row.robotId), '已暂停机器人后续接单')
    },
    async handleException(row, handlingStatus) {
      const title = handlingStatus === 'RESOLVED' ? '解决异常' : '确认异常'
      try {
        const result = await this.$prompt('请填写处理说明，内容会进入审计记录。', title, { inputType: 'textarea', inputValidator: value => Boolean(value && value.trim()) || '处理说明不能为空' })
        await this.runAction(() => handleAdminOperationException({ category: row.category, robotId: row.robotId, taskId: row.taskId, orderId: row.orderId, handlingStatus, handlingNote: result.value.trim() }), handlingStatus === 'RESOLVED' ? '异常已标记解决' : '异常已确认')
      } catch (error) { if (error !== 'cancel' && error !== 'close') this.$message.error(error.userMessage || getApiErrorMessage(error, '异常处置失败')) }
    },
    async saveThresholds() {
      this.savingThresholds = true
      try {
        const response = await saveDerasSettings({ ...this.thresholds, ...this.thresholdForm })
        if (response.data.code !== 200) { this.$message.error(response.data.msg || '阈值保存失败'); return }
        this.$message.success('异常判断阈值已保存')
        await this.loadExceptions()
      } catch (error) { this.$message.error(error.userMessage || getApiErrorMessage(error, '阈值保存失败')) } finally { this.savingThresholds = false }
    },
    async runAction(action, successText) {
      try { const response = await action(); if (response.data.code !== 200) { this.$message.warning(response.data.msg || '操作未完成'); return } this.$message.success(successText); await this.loadExceptions() }
      catch (error) { this.$message.error(error.userMessage || getApiErrorMessage(error, '操作请求失败')) }
    }
  }
}
</script>

<style scoped>
.exception-page { min-height: 100%; padding: 30px; color: #e9f2f6; background: #07131d; }
.page-header,.panel-head { display:flex; justify-content:space-between; gap:24px; align-items:flex-start; }
.page-header h1,.panel-head h2 { margin:6px 0 8px; color:#fff; font-size:28px; letter-spacing:0; }
.page-header p:not(.eyebrow) { max-width:760px; margin:0; color:#99afbd; line-height:1.7; }
.eyebrow { margin:0; color:#79dab0; font-size:11px; font-weight:800; letter-spacing:0.12em; }
.primary-btn { min-width:112px; height:38px; border:0; border-radius:5px; color:#03130f; cursor:pointer; background:#83e6bc; font-weight:800; }
.primary-btn:disabled { cursor:not-allowed; opacity:.6; }
.metric-grid { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:14px; margin:26px 0 14px; }
.metric-card { min-height:112px; padding:18px; border:1px solid #254350; border-radius:6px; background:#0b1b26; }
.metric-card span,.metric-card p { display:block; margin:0; color:#94aab7; font-size:13px; }
.metric-card strong { display:block; margin:9px 0 7px; color:#fff; font-size:30px; }
.metric-card.critical { border-color:rgba(242,112,112,.56); }.metric-card.critical strong { color:#ff9a9a; }
.metric-card.high { border-color:rgba(246,188,91,.55); }.metric-card.high strong { color:#ffd58d; }
.metric-card.medium { border-color:rgba(115,177,239,.5); }.metric-card.medium strong { color:#9dceff; }
.category-strip { display:flex; flex-wrap:wrap; gap:10px; margin:16px 0 20px; }
.category-strip button { min-width:120px; display:flex; justify-content:space-between; align-items:center; gap:12px; padding:10px 13px; border:1px solid #284656; border-radius:5px; color:#b9cbd5; cursor:pointer; background:#0b1b26; }
.category-strip button.active { color:#041713; border-color:#80e7bc; background:#80e7bc; }.category-strip strong { font-size:16px; }
.table-panel { padding:20px; border:1px solid #24414f; border-radius:6px; background:#0b1b26; }.panel-head { margin-bottom:18px; }.panel-head h2 { font-size:20px; }.panel-head > span { color:#8299a7; font-size:13px; }
.threshold-panel { display:grid; grid-template-columns:minmax(180px,1fr) auto auto auto; gap:18px; align-items:center; margin-bottom:20px; padding:18px 20px; border:1px solid #24414f; border-radius:6px; background:#0b1b26; }
.threshold-panel h2 { margin:6px 0 0; font-size:18px; }.threshold-panel label { display:flex; gap:8px; align-items:center; color:#a9bec9; }.threshold-panel label span { white-space:nowrap; }.threshold-panel em { color:#76909e; font-size:12px; font-style:normal; }.history-panel { margin-top:20px; }
::v-deep(.el-table),::v-deep(.el-table tr),::v-deep(.el-table th),::v-deep(.el-table td) { color:#dce8ee; background:#0b1b26; border-color:#203c49; } ::v-deep(.el-table th) { color:#8fb2c2; } ::v-deep(.el-table--enable-row-hover .el-table__body tr:hover>td) { background:#102632; }
small { display:block; margin-top:5px; color:#8ea5b2; line-height:1.45; }.suggestion { color:#b9cbd5; line-height:1.55; }
@media (max-width: 980px) { .exception-page { padding:20px; }.metric-grid { grid-template-columns:repeat(2,minmax(0,1fr)); }.page-header { flex-direction:column; }.threshold-panel { grid-template-columns:1fr; align-items:start; }.table-panel { overflow-x:auto; } }
@media (max-width: 560px) { .metric-grid { grid-template-columns:1fr; }.category-strip button { flex:1 1 42%; min-width:0; } }
</style>
